import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import QuickView from "@/components/QuickView";
import Reveal from "@/components/Reveal";
import Particles from "@/components/Particles";

const CONCERN_LABELS = {
  acne: "Acne-Prone",
  "uneven-tone": "Uneven Tone",
  dryness: "Dryness",
  "hair-care": "Hair Care",
  aging: "Aging",
  glow: "Glow",
};
const TYPES = ["Serum", "Face Oil", "Moisturizer", "Cleanser", "Mask", "Toner", "Eye Care", "Hair Oil", "Exfoliant", "Gift Set"];
const SORTS = [
  { value: "popular", label: "Bestselling" },
  { value: "newest", label: "Newest" },
  { value: "rating", label: "Top Rated" },
  { value: "price-asc", label: "Price: Low → High" },
  { value: "price-desc", label: "Price: High → Low" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickView, setQuickView] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [q, setQ] = useState("");

  const filters = {
    concern: params.get("concern") || "",
    type: params.get("type") || "",
    skin_type: params.get("skin_type") || "",
    max_price: params.get("max_price") || "",
    sort: params.get("sort") || "popular",
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    setLoading(true);
    const query = {};
    Object.entries(filters).forEach(([k, v]) => v && (query[k] = v));
    if (q.trim()) query.q = q.trim();
    api.getProducts(query).then((d) => { setProducts(d); setLoading(false); }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, q]);

  const FilterPanel = (
    <div className="space-y-7" data-testid="filters-panel">
      <div>
        <p className="eyebrow mb-3">Concern</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CONCERN_LABELS).map(([v, label]) => (
            <button
              key={v}
              data-testid={`filter-concern-${v}`}
              onClick={() => setFilter("concern", filters.concern === v ? "" : v)}
              className="rounded-full border px-3.5 py-1.5 text-xs transition-all"
              style={{
                borderColor: filters.concern === v ? "var(--mn-mint)" : "var(--mn-glass-border)",
                color: filters.concern === v ? "var(--mn-mint)" : "var(--mn-text2)",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-3">Product Type</p>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((v) => (
            <button
              key={v}
              data-testid={`filter-type-${v.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setFilter("type", filters.type === v ? "" : v)}
              className="rounded-full border px-3.5 py-1.5 text-xs transition-all"
              style={{
                borderColor: filters.type === v ? "var(--mn-gold)" : "var(--mn-glass-border)",
                color: filters.type === v ? "var(--mn-gold)" : "var(--mn-text2)",
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-3">Skin Type</p>
        <div className="flex flex-wrap gap-2">
          {["oily", "dry", "combination", "normal", "sensitive"].map((v) => (
            <button
              key={v}
              data-testid={`filter-skin-${v}`}
              onClick={() => setFilter("skin_type", filters.skin_type === v ? "" : v)}
              className="rounded-full border px-3.5 py-1.5 text-xs capitalize transition-all"
              style={{
                borderColor: filters.skin_type === v ? "var(--mn-mint)" : "var(--mn-glass-border)",
                color: filters.skin_type === v ? "var(--mn-mint)" : "var(--mn-text2)",
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-3">Max Price · {filters.max_price ? `₹${Number(filters.max_price).toLocaleString("en-IN")}` : "Any"}</p>
        <input
          data-testid="filter-price-range"
          type="range"
          min="500"
          max="2500"
          step="100"
          value={filters.max_price || 2500}
          onChange={(e) => setFilter("max_price", e.target.value === "2500" ? "" : e.target.value)}
          className="w-full accent-[#D4AF37]"
        />
      </div>
      {(filters.concern || filters.type || filters.skin_type || filters.max_price) && (
        <button
          data-testid="filters-clear"
          onClick={() => setParams({}, { replace: true })}
          className="flex items-center gap-1.5 text-xs underline-offset-4 hover:underline"
          style={{ color: "var(--mn-terra)" }}
        >
          <X size={13} /> Clear all filters
        </button>
      )}
    </div>
  );

  return (
    <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-6 lg:px-8" data-testid="shop-page">
      <Particles count={8} />
      <Reveal>
        <p className="eyebrow">The Apothecary</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl lg:text-5xl">
          Every formula. <span className="gold-text italic">One house.</span>
        </h1>
      </Reveal>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="glass flex flex-1 items-center gap-2 rounded-full px-4" style={{ minWidth: 220 }}>
          <Search size={15} style={{ color: "var(--mn-gold)" }} />
          <input
            data-testid="shop-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search serums, saffron, niacinamide…"
            className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-[var(--mn-text2)]"
          />
        </div>
        <select
          data-testid="shop-sort-select"
          value={filters.sort}
          onChange={(e) => setFilter("sort", e.target.value)}
          className="glass rounded-full px-4 py-3 text-sm outline-none"
          style={{ background: "var(--mn-glass)", color: "var(--mn-text)" }}
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value} style={{ background: "#0D1812" }}>{s.label}</option>
          ))}
        </select>
        <button
          data-testid="filters-toggle"
          onClick={() => setFiltersOpen((o) => !o)}
          className="btn-outline-glow flex items-center gap-2 rounded-full px-4 py-3 text-sm lg:hidden"
        >
          <SlidersHorizontal size={15} /> Filters
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[240px_1fr]">
        <aside className={`${filtersOpen ? "block" : "hidden"} lg:block`}>{FilterPanel}</aside>
        <div>
          <p className="mb-5 font-mono text-[0.65rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-text2)" }} data-testid="results-count">
            {loading ? "Consulting the grimoire…" : `${products.length} formulas found`}
          </p>
          {!loading && products.length === 0 ? (
            <div className="glass-card rounded-3xl p-14 text-center" data-testid="no-results">
              <p className="font-serif text-xl">No formulas match that combination.</p>
              <p className="mt-2 text-sm" style={{ color: "var(--mn-text2)" }}>Loosen a filter or two — your ritual is close.</p>
            </div>
          ) : (
            <motion.div layout className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="product-grid">
              {products.map((p, i) => (
                <Reveal key={p.slug} delay={Math.min(i * 0.05, 0.4)} y={20}>
                  <ProductCard product={p} onQuickView={setQuickView} />
                </Reveal>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      <QuickView product={quickView} onClose={() => setQuickView(null)} />
    </div>
  );
}
